import classNames from "classnames";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useWindowSize } from "hooks/useWindowSize";
import { useCallback, useEffect, useRef, useState } from "react";
import { DeleteEntityConfirmation } from "signInSignUp/DeleteEntityConfirmation";
import { useEntityCreationContext } from "signInSignUp/EntityCreationContext";
import { EntityDataType } from "signInSignUp/EntityCreationForm";
import { UnifiedPreviewWithoutGroup } from "signInSignUp/UnifiedPreviewWithoutGroup";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { buildEntityAddressPayload } from "util/buildEntityAddressPayload";
import { EMPTY_ADDRESS_FIELDS } from "v2/components/AddressPicker/AddressPicker";
import { LLC_TAX_OPTIONS } from "v2/components/onboarding/steps/EntityTypeStep/entityOptions";
import { buildAddressString } from "v2/components/onboarding/steps/ConfirmEntityStep/entityFieldOptions";
import { EntityFormValues, ManualMode } from "./modes/ManualMode";
import { SavedMode } from "./modes/SavedMode";
import { SelectionMode } from "./modes/SelectionMode";
import { UploadedMode } from "./modes/UploadedMode";

export type InkleFileData = {
  name: string;
  type: string;
  uuid?: string;
};

type EntityBoxPropsType = {
  entityIndex: number;
  onDelete: () => void;
  groupId?: string;
  onSave?: (val: EntityDataType) => void;
  hasError: boolean;
};

export const EntityBox = ({
  entityIndex,
  onDelete,
  onSave,
  hasError,
}: EntityBoxPropsType) => {
  const { values, setFieldValue, initialValues } = useFormikContext<{
    entityData: EntityDataType[];
  }>();
  const { data: countries = [] } = useGetCountriesDataQuery();
  const [entryMode, setEntryMode] = useState<
    "selection" | "uploaded" | "manual" | "saved"
  >("selection");
  const [localPreviewFile, setLocalPreviewFile] = useState<File | null>(null);
  const [remotePreviewFile, setRemotePreviewFile] =
    useState<InkleFileData | null>(null);
  const [coiFile, setCoiFile] = useState<File | null>(null);
  const [parsedCOIData, setParsedCOIData] = useState<{
    entityName: string;
    country: string;
    state: string;
    formationType: string;
    dateOfIncorporation: string | null;
    registeredAgent: string | null;
  } | null>(null);
  const hasInitialized = useRef(false);
  const entityRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useWindowSize();
  const deleteEntity = useModal();
  const { isFromAdmin } = useEntityCreationContext();

  const currentEntity = values.entityData[entityIndex];
  const initialEntity = initialValues.entityData[entityIndex];
  const selectedCountryId = currentEntity?.country;

  const entityKey = `${entityIndex}-${currentEntity?.uuid || "new"}`;
  const prevEntityKeyRef = useRef(entityKey);

  if (prevEntityKeyRef.current !== entityKey) {
    hasInitialized.current = false;
    prevEntityKeyRef.current = entityKey;
  }

  useEffect(() => {
    if (hasInitialized.current) return;

    if (currentEntity?.isSaved) {
      setEntryMode("saved");
    } else if (currentEntity?.showEntrySelection) {
      setEntryMode("selection");
    } else {
      setEntryMode("manual");
    }

    hasInitialized.current = true;
  }, [currentEntity?.isSaved, currentEntity?.showEntrySelection, entityKey]);

  const selectedCountry = countries.find(
    ({ uuid }) => uuid === selectedCountryId
  );

  const isUSCountry = selectedCountry?.code_alpha_2 === "US";
  const usCountryId = countries.find(
    ({ code_alpha_2 }) => code_alpha_2 === "US"
  )?.uuid;

  const handleEdit = () => {
    setEntryMode("manual");
  };

  const handleDeleteFlow = (isDirty: boolean) => {
    if (isDirty) {
      deleteEntity.open();
    } else {
      onDelete();
    }
  };

  const handleCancel = (isDirty: boolean = false) => {
    if (currentEntity?.isSaved) {
      setEntryMode("saved");
    } else {
      handleDeleteFlow(isDirty);
    }
  };

  const handleUploadCoi = (file: File) => {
    setCoiFile(file);
    setEntryMode("uploaded");
  };

  const handleDeleteCoi = () => {
    setCoiFile(null);
    setParsedCOIData(null);
    setEntryMode("selection");
  };

  const handleEnterManually = () => {
    setCoiFile(null);
    setParsedCOIData(null);
    setEntryMode("manual");
  };

  const handleCancelUpload = () => {
    setCoiFile(null);
    setParsedCOIData(null);
    setEntryMode("selection");
  };

  const handleCoiFileClick = () => {
    if (coiFile) {
      onPreview({ file: coiFile });
    }
  };

  const handleMarkAsEditing = useCallback(() => {
    setFieldValue(`entityData[${entityIndex}].isEditing`, true);
  }, [setFieldValue, entityIndex]);

  const onPreview = useCallback(
    ({ file }: { file: File | InkleFileData | null }) => {
      if (!file) return;

      if ((file as InkleFileData)?.uuid) {
        setRemotePreviewFile(file as InkleFileData);
      } else {
        setLocalPreviewFile(file as File);
      }
    },
    []
  );

  const handlePreviewClose = useCallback(() => {
    setLocalPreviewFile(null);
    setRemotePreviewFile(null);
  }, []);

  const handleSaveEntity = (values: EntityFormValues) => {
    const updatedEntity = {
      ...currentEntity,
      ...values,
      isSaved: true,
      isEditing: false,
      uuid: currentEntity?.uuid || crypto.randomUUID(),
      showEntrySelection: false,
    };

    setEntryMode("saved");
    setTimeout(() => onSave?.(updatedEntity), 100);
  };

  const handleDeleteConfirm = () => {
    onDelete();
    deleteEntity.close();
  };

  const disableDelete = values.entityData.length <= 1;

  const renderContent = () => {
    if (entryMode === "selection") {
      return (
        <SelectionMode
          disableDelete={disableDelete}
          onDelete={onDelete}
          onUpload={handleUploadCoi}
          onEnterManually={handleEnterManually}
        />
      );
    }

    if (entryMode === "uploaded") {
      return (
        <UploadedMode
          disableDelete={disableDelete}
          onDelete={onDelete}
          coiFile={coiFile}
          onDrop={(files) => {
            if (files.length > 0) {
              setCoiFile(files[0] as File);
            }
          }}
          onDeleteCoi={handleDeleteCoi}
          onFileClick={handleCoiFileClick}
          onCancel={handleCancelUpload}
          setEntryMode={setEntryMode}
          setParsedCOIData={setParsedCOIData}
        />
      );
    }

    if (entryMode === "saved") {
      const selectedState = countries
        .find((c) => c.uuid === currentEntity.country)
        ?.states?.find((s) => s.uuid === currentEntity.state);

      const formationType = countries
        .find((c) => c.uuid === currentEntity.country)
        ?.formation_type?.find((f) => f.uuid === currentEntity.formationType);

      const countryData = countries.find(
        (c) => c.uuid === currentEntity.country
      );

      const taxClassification = LLC_TAX_OPTIONS.find(
        ({ key }) => key === currentEntity.taxedAs
      )?.name;

      const addressPayload = buildEntityAddressPayload(
        currentEntity.country,
        currentEntity.address
      );

      return (
        <SavedMode
          entityName={currentEntity.entityName}
          suffix={currentEntity.suffix}
          countryCode={countryData?.code_alpha_2}
          countryName={countryData?.name}
          stateName={selectedState?.name}
          formationTypeName={formationType?.name}
          isUSCountry={isUSCountry}
          einFile={currentEntity.ein}
          coiFile={currentEntity.coiFile}
          onEdit={handleEdit}
          onDelete={deleteEntity.open}
          onPreview={(file: File) => onPreview({ file })}
          isMobile={isMobile}
          taxClassification={taxClassification}
          dateOfIncorporation={
            isFromAdmin ? currentEntity.dateOfIncorporation : null
          }
          businessAddress={
            addressPayload ? buildAddressString(addressPayload) : undefined
          }
        />
      );
    }

    return (
      <ManualMode
        disableDelete={disableDelete}
        onDelete={handleDeleteFlow}
        onSave={handleSaveEntity}
        onCancel={handleCancel}
        initialValues={{
          country: parsedCOIData?.country || currentEntity.country,
          state: parsedCOIData?.state || currentEntity.state,
          formationType:
            parsedCOIData?.formationType || currentEntity.formationType,
          entityName: parsedCOIData?.entityName || currentEntity.entityName,
          suffix: currentEntity.suffix,
          ein: currentEntity.ein,
          coiFile: coiFile,
          dateOfIncorporation:
            parsedCOIData?.dateOfIncorporation ||
            currentEntity.dateOfIncorporation,
          registeredAgent: parsedCOIData?.registeredAgent || null,
          taxedAs: currentEntity.taxedAs ?? "",
          address: currentEntity.address ?? EMPTY_ADDRESS_FIELDS,
        }}
        countries={countries}
        usCountryId={usCountryId}
        isSaved={Boolean(currentEntity.isSaved)}
        onEdit={handleMarkAsEditing}
        onPreview={(file: File) => onPreview({ file })}
      />
    );
  };

  return (
    <>
      <div
        ref={entityRef}
        data-entity-index={entityIndex}
        className={classNames("t-transition-all t-duration-300")}
      >
        {renderContent()}
      </div>
      <UnifiedPreviewWithoutGroup
        localPreviewFile={localPreviewFile}
        remotePreviewFile={remotePreviewFile}
        closeModal={handlePreviewClose}
      />
      <DeleteEntityConfirmation
        {...deleteEntity}
        onClick={handleDeleteConfirm}
      />
    </>
  );
};
